import React, { useCallback, useEffect, useState } from 'react';
import { useOnboarding } from './ImportJobContext';
import { onboardingApi } from './onboarding.service';
import { ONBOARDING_ENABLED } from './flags';
import toast from '../shared/Toast';
import './ImportSection.css';

/**
 * Clients → Import (plan §4.5). Replaces the old "contact support" stub.
 * One button opens the same import layer; below it, the import history:
 * date · files · what came in · status · Undo (while inside the 72h window).
 */

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch (e) { return iso; }
};
const STATUS_LABEL = { extracting: 'Reading', extracted: 'Ready to add', committing: 'Adding', committed: 'Added', rolled_back: 'Undone', failed: 'Failed' };

export default function ImportSection({ historyOnly = false }) {
  const ob = useOnboarding();
  const [jobs, setJobs] = useState(null);
  const [undoing, setUndoing] = useState('');
  const layerOpen = ob?.layer?.open;

  const load = useCallback(async () => {
    try { setJobs(await onboardingApi.listJobs()); } catch (e) { setJobs([]); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Refresh the list when the layer closes (a new import may have committed).
  useEffect(() => { if (!layerOpen) load(); }, [layerOpen, load]);

  if (!ONBOARDING_ENABLED || !ob) return null;

  const undo = async (jobId) => {
    if (!window.confirm('Undo this import? Everything it added will be removed. Anything you have edited since stays.')) return;
    setUndoing(jobId);
    try { await ob.rollback(jobId); toast.success('Import undone'); await load(); }
    catch (e) { toast.error(e.message || 'Could not undo. Please try again.'); }
    setUndoing('');
  };

  const summaryText = (s = {}) => [
    s.clients && `${s.clients.toLocaleString('en-GB')} clients`,
    s.services && `${s.services.toLocaleString('en-GB')} services`,
    s.products && `${s.products.toLocaleString('en-GB')} products`,
    s.staff && `${s.staff.toLocaleString('en-GB')} staff`,
  ].filter(Boolean).join(' · ') || 'Nothing read yet';

  return (
    <div className={`import-section${historyOnly ? '' : ' mt-4'}`}>
      <div className="section-card">
        {!historyOnly && <div className="section-header">
          <div>
            <h4>Import anything</h4>
            <p className="text-muted mb-0">Client lists, price lists, product sheets — a spreadsheet, a PDF or a photo of the price list. We work out what each one is and merge it with what you already have.</p>
          </div>
          <button type="button" className="btn btn-primary" onClick={() => ob.openLayer({ mode: 'import' })}>
            <i className="fa fa-upload mr-2"></i>
            Import
          </button>
        </div>}

        {!historyOnly && <div className="import-info mt-3">
          <h5>Good to know</h5>
          <ul>
            <li>CSV, Excel, PDF and photos. Up to 10 files, 25 MB each.</li>
            <li>Duplicates are matched on phone and email and offered to you to merge, never merged silently.</li>
            <li>Nothing is sent to your clients, and marketing consent is never assumed.</li>
            <li>Every import can be undone for 72 hours.</li>
          </ul>
        </div>}

        <div className="imp-history">
          <h5>Import history</h5>
          {jobs === null && <div className="imp-empty">Loading…</div>}
          {jobs && jobs.length === 0 && <div className="imp-empty">No imports yet.</div>}
          {jobs && jobs.length > 0 && (
            <div className="imp-table-wrap">
              <table className="imp-table">
                <thead><tr><th>When</th><th>Files</th><th>What came in</th><th>Status</th><th /></tr></thead>
                <tbody>
                  {jobs.map((j) => (
                    <tr key={j.jobId}>
                      <td className="imp-nowrap">{fmtDate(j.createdAt)}</td>
                      <td title={(j.files || []).join(', ')}>{(j.files || []).length} file{(j.files || []).length === 1 ? '' : 's'}</td>
                      <td>{summaryText(j.summary)}</td>
                      <td><span className={`imp-status imp-status--${j.status}`}>{STATUS_LABEL[j.status] || j.status}</span></td>
                      <td className="imp-nowrap">
                        {j.canRollback && (
                          <button type="button" className="imp-undo" disabled={undoing === j.jobId} onClick={() => undo(j.jobId)}>{undoing === j.jobId ? 'Undoing…' : 'Undo'}</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
